<template>
  <section id="advantages" class="advantages-section">
    <v-container class="section-container">
      <div class="section-header text-center">
        <span class="section-tag">Преимущества платформы</span>
        <h2 class="section-title">Почему рестораторы выбирают OZO.direct</h2>
        <p class="section-description">
          Комплексный технологический фундамент, снижающий издержки на
          агрегаторы и кратно повышающий возвращаемость гостей.
        </p>
      </div>

      <!-- Bento Grid Layout с реальными мокапами из презентации -->
      <div class="bento-grid mt-10">
        <!-- Карточка 1: Управление базой и RFM-анализ (Большая) -->
        <div class="bento-item bento-item--large">
          <div class="bento-content">
            <div class="bento-badge">Управление базой гостей</div>
            <h3 class="bento-title">
              Умная программа лояльности на базе RFM-анализа
            </h3>
            <p class="bento-text">
              Автоматическая сегментация клиентской базы по давности (Recency),
              частоте (Frequency) и сумме покупок (Monetary). Внедряйте
              фиксированную или прогрессивную шкалу бонусов и кешбэков без
              пластиковых карт.
            </p>
            <div class="rfm-metrics-preview">
              <div class="metric-pill">
                <span class="metric-val">+25%</span>
                <span class="metric-lbl">Средний чек лояльных гостей</span>
              </div>
              <div class="metric-pill">
                <span class="metric-val">3.2x</span>
                <span class="metric-lbl">Частота повторных визитов</span>
              </div>
            </div>
          </div>
          <div class="bento-thumb img-skeleton-box">
            <img
              :src="require('@/assets/presentation/loyalty-mockup-1.png')"
              alt="Программа лояльности OZO"
              class="thumb-img"
              loading="lazy"
              @load="onImgLoad"
            />
          </div>
        </div>

        <!-- Карточка 2: Собственная доставка (Экономия 20%) -->
        <div class="bento-item bento-item--accent">
          <div class="bento-content">
            <div class="bento-badge accent-badge">Собственная доставка</div>
            <h3 class="bento-title">Экономия до 20% на каждом заказе</h3>
            <p class="bento-text">
              Принимайте заказы на доставку и навынос напрямую в приложении.
              Оплата сразу на счет заведения без посредников. Прямая интеграция
              с Яндекс Доставкой и настройка зон.
            </p>
            <div class="saving-badge">
              <span class="saving-num">-20%</span>
              <span class="saving-txt"
                >комиссии по сравнению с агрегаторами</span
              >
            </div>
          </div>
          <div class="bento-thumb img-skeleton-box mt-4">
            <img
              :src="require('@/assets/presentation/delivery-mockup.png')"
              alt="Модуль доставки OZO"
              class="thumb-img"
              loading="lazy"
              @load="onImgLoad"
            />
          </div>
        </div>

        <!-- Карточка 3: Интерактивный просмотр видов купонов и сторис (Слайды 6 и 7) -->
        <div class="bento-item bento-item--full">
          <div class="bento-content">
            <div class="d-flex align-center justify-space-between flex-wrap">
              <div>
                <div class="bento-badge">Маркетинг & Автоворонки</div>
                <h3 class="bento-title">
                  Интерактивные сторис и умные виды купонов
                </h3>
                <p class="bento-text">
                  Стимулируйте гостей делать заказы в нужный момент. Выберите
                  механику для просмотра:
                </p>
              </div>

              <!-- Переключатель механик маркетинга -->
              <div class="coupon-tabs mt-2 mb-4">
                <button
                  v-for="item in marketingMechanics"
                  :key="item.id"
                  class="coupon-tab-btn"
                  :class="{ active: activeMechanicId === item.id }"
                  @click="activeMechanicId = item.id"
                >
                  <i :class="[item.icon, 'mr-1']"></i>
                  <span>{{ item.tabTitle }}</span>
                </button>
              </div>
            </div>

            <!-- Интерактивная демонстрация активной механики с мокапом из презентации -->
            <transition name="fade" mode="out-in">
              <div :key="activeMechanic.id" class="marketing-showcase">
                <div class="showcase-visual img-skeleton-box">
                  <img
                    :src="activeMechanic.image"
                    :alt="activeMechanic.title"
                    class="showcase-phone-img"
                    loading="lazy"
                    @load="onImgLoad"
                  />
                </div>

                <div class="showcase-info">
                  <div class="showcase-top">
                    <span class="badge-accent">{{ activeMechanic.tag }}</span>
                    <span class="badge-type">{{ activeMechanic.type }}</span>
                  </div>
                  <h4 class="showcase-title">{{ activeMechanic.title }}</h4>
                  <p class="showcase-desc">{{ activeMechanic.description }}</p>

                  <div class="showcase-bullets">
                    <div
                      v-for="(point, pIdx) in activeMechanic.points"
                      :key="pIdx"
                      class="point-item"
                    >
                      <i class="ri-checkbox-circle-fill point-icon"></i>
                      <span>{{ point }}</span>
                    </div>
                  </div>

                  <div class="showcase-stats mt-4">
                    <div class="stat-box">
                      <span class="stat-num">{{ activeMechanic.statVal }}</span>
                      <span class="stat-label">{{
                        activeMechanic.statLabel
                      }}</span>
                    </div>
                    <div class="stat-box">
                      <span class="stat-num">{{
                        activeMechanic.statSecond
                      }}</span>
                      <span class="stat-label">{{
                        activeMechanic.statSecondLabel
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <!-- Карточка 4: Бронирование столов (Слайд 9) -->
        <div class="bento-item bento-item--large">
          <div class="bento-content">
            <div class="bento-badge">Бронирование</div>
            <h3 class="bento-title">Онлайн-бронирование столов без комиссий</h3>
            <p class="bento-text">
              Принимайте заявки на бронирование с предоплатой или без.
              Электронный журнал бронирования в приложении ресторана,
              напоминания персоналу и экономия на внешних сервисах.
            </p>
            <div class="booking-features mt-4">
              <span class="feat-pill"
                ><i class="ri-booklet-line mr-1"></i>Журнал столов</span
              >
              <span class="feat-pill"
                ><i class="ri-smartphone-line mr-1"></i>Сторис с бронью</span
              >
              <span class="feat-pill"
                ><i class="ri-radar-line mr-1"></i>Анализ поведения</span
              >
            </div>
          </div>
          <div class="bento-thumb img-skeleton-box">
            <img
              :src="require('@/assets/presentation/booking-mockup.png')"
              alt="Бронирование столов OZO"
              class="thumb-img"
              loading="lazy"
              @load="onImgLoad"
            />
          </div>
        </div>

        <!-- Карточка 5: Выгоды ресторатора (Слайд 14) -->
        <div class="bento-item bento-item--accent">
          <div class="bento-content">
            <div class="bento-badge accent-badge">Экономический эффект</div>
            <h3 class="bento-title">Выгоды внедрения OZO.direct</h3>
            <ul class="benefits-compact-list">
              <li>
                <strong>+25%</strong>
                <span>Рост выручки за счет повторных визитов гостей</span>
              </li>
              <li>
                <strong>20%</strong>
                <span>Экономия на каждом заказе доставки</span>
              </li>
              <li>
                <strong>0 ₸</strong>
                <span
                  >Затрат на собственную разработку мобильных приложений</span
                >
              </li>
              <li>
                <strong>100%</strong>
                <span>База гостей принадлежит только вашему заведению</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </v-container>
  </section>
</template>

<script>
export default {
  name: "AdvantagesSection",
  data() {
    return {
      activeMechanicId: "stories",
      marketingMechanics: [
        {
          id: "stories",
          tabTitle: "Интерактивные сторис",
          icon: "ri-movie-line",
          title: "Маркетинговые кампании через Stories",
          tag: "Интерактивный формат",
          type: "Push + Stories",
          image: require("@/assets/presentation/stories-mockup-1.png"),
          description:
            "Гость получает push-уведомление и переходит в приложение. Сторис открывается сразу при входе с сочными видео и интерактивными кнопками действия.",
          points: [
            "Кнопки прямого действия: «Заказать блюдо», «Забронировать стол»",
            "Анонсы сезонных новинок и спецпредложений от шефа",
            "Анализ конверсии и просмотров в OZO.OFFICE",
          ],
          statVal: "+35%",
          statLabel: "Просмотры новинок меню",
          statSecond: "2.4x",
          statSecondLabel: "Конверсия кнопки заказа",
        },
        {
          id: "birthday",
          tabTitle: "Купон на День Рождения",
          icon: "ri-cake-2-line",
          title: "Автоматический купон на праздник",
          tag: "Персональный триггер",
          type: "Автоворонка",
          image: require("@/assets/presentation/coupon-mockup-1.png"),
          description:
            "Система автоматически отправляет персональное поздравление и подарок в нужный момент. Побуждает гостя отметить праздник в вашем ресторане.",
          points: [
            "Действует за 3 дня до и 7 дней после дня рождения",
            "Ограничения по каналам, месту продаж и сроку",
            "Автоматический триггер без участия администратора",
          ],
          statVal: "+42%",
          statLabel: "Рост банкетных броней",
          statSecond: "₸ 45 000",
          statSecondLabel: "Средний банкетный чек",
        },
        {
          id: "product",
          tabTitle: "Продуктовый купон",
          icon: "ri-gift-line",
          title: "Купон на конкретное блюдо или категорию",
          tag: "Драйвер среднего чека",
          type: "Скидка / Подарок",
          image: require("@/assets/presentation/coupon-mockup-2.png"),
          description:
            "Продвигайте блюда высокой маржинальности или мотивируйте гостей добирать заказ до нужной суммы (например, «Десерт в подарок при чеке от 6 000 ₸»).",
          points: [
            "Форматы: на сумму, скидка в процентах или подарок",
            "Автоматическая привязка к составу чека в корзине",
            "Ручные купоны для быстрой компенсации гостю",
          ],
          statVal: "+18%",
          statLabel: "Рост среднего чека",
          statSecond: "82%",
          statSecondLabel: "Маржа акционных позиций",
        },
        {
          id: "accumulative",
          tabTitle: "Накопительный (5+1)",
          icon: "ri-medal-line",
          title: "Накопительные механики «Каждый 5-й в подарок»",
          tag: "Удержание гостей",
          type: "Программа 5+1",
          image: require("@/assets/presentation/coupon-mockup-3.png"),
          description:
            "Современная замена картонным штампам. Гость видит свой прогресс прямо в смартфоне и выбирает ваше заведение для ежедневных покупок.",
          points: [
            "«Каждый пятый ланч или кофе бесплатно»",
            "Мотивация частых повторных визитов в кофейни и бары",
            "Прозрачный учет в кассовой системе iiko / r_keeper",
          ],
          statVal: "4.8x",
          statLabel: "Частота повторных чеков",
          statSecond: "78%",
          statSecondLabel: "Завершают накопление",
        },
      ],
    };
  },
  computed: {
    activeMechanic() {
      return (
        this.marketingMechanics.find((m) => m.id === this.activeMechanicId) ||
        this.marketingMechanics[0]
      );
    },
  },
  mounted() {
    this.checkImagesLoaded();
  },
  updated() {
    this.$nextTick(() => {
      this.checkImagesLoaded();
    });
  },
  methods: {
    onImgLoad(e) {
      if (e && e.target) {
        e.target.classList.add("is-loaded");
        if (e.target.parentElement) {
          e.target.parentElement.classList.add("is-loaded");
        }
      }
    },
    checkImagesLoaded() {
      this.$nextTick(() => {
        this.$el
          .querySelectorAll(".thumb-img, .showcase-phone-img")
          .forEach((img) => {
            if (img.complete && img.naturalWidth > 0) {
              img.classList.add("is-loaded");
              if (img.parentElement)
                img.parentElement.classList.add("is-loaded");
            }
          });
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/_variables.scss";

.advantages-section {
  background-color: $color-bg-white;
  padding: $section-py 0;

  @media (max-width: 960px) {
    padding: $section-py-mobile 0;
  }
}

.section-container {
  max-width: $container-max-width;
  margin: 0 auto;
}

.section-tag {
  display: inline-block;
  color: #0b7a3a;
  background: $color-neon-green-soft;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 6px 14px;
  border-radius: $radius-full;
  margin-bottom: 12px;
}

.section-title {
  font-family: $font-family-heading;
  font-size: 38px;
  font-weight: 800;
  color: $color-text-dark;
  margin-bottom: 16px;

  @media (max-width: 960px) {
    font-size: 28px;
  }
}

.section-description {
  font-size: 16px;
  color: $color-text-secondary;
  max-width: 680px;
  margin: 0 auto;
  line-height: 1.6;
}

/* Bento Grid */
.bento-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 24px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
}

.bento-item {
  background: #fcfdfe;
  border: 1px solid #e9ecef;
  border-radius: $radius-lg;
  padding: 36px 32px;
  transition: all 0.3s cubic-bezier(0.2, 0, 0, 1);
  grid-column: span 6;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 600px) {
    padding: 24px 18px;
  }

  @media (max-width: 480px) {
    padding: 20px 14px;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: $shadow-hover;
    border-color: rgba(0, 230, 118, 0.4);
  }

  &--large {
    grid-column: span 7;

    @media (max-width: 960px) {
      grid-column: span 1;
    }
  }

  &--accent {
    grid-column: span 5;
    background: $color-graphite-dark;
    color: $color-text-light;
    border-color: #2a2d33;

    .bento-title {
      color: $color-text-light;
    }

    .bento-text {
      color: #94a3b8;
    }

    .bento-thumb.img-skeleton-box::before {
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.05) 0%,
        rgba(255, 255, 255, 0.15) 50%,
        rgba(255, 255, 255, 0.05) 100%
      );
    }

    @media (max-width: 960px) {
      grid-column: span 1;
    }
  }

  &--full {
    grid-column: span 12;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-color: #e2e8f0;

    @media (max-width: 960px) {
      grid-column: span 1;
    }
  }

  @media (max-width: 960px) {
    grid-column: span 1;
  }
}

.bento-thumb {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  border-radius: $radius-md;

  .thumb-img {
    max-width: 100%;
    max-height: 220px;
    object-fit: contain;
    border-radius: $radius-md;
    filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.08));
    transition: transform 0.3s ease;
  }

  &:hover .thumb-img {
    transform: scale(1.03);
  }
}

.bento-badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $color-text-secondary;
  margin-bottom: 14px;

  &.accent-badge {
    color: $color-neon-green;
  }
}

.bento-title {
  font-family: $font-family-heading;
  font-size: 22px;
  font-weight: 700;
  color: $color-text-dark;
  margin-bottom: 12px;
  line-height: 1.3;
}

.bento-text {
  font-size: 14px;
  line-height: 1.6;
  color: $color-text-secondary;
  margin-bottom: 16px;
}

.rfm-metrics-preview {
  display: flex;
  gap: 16px;
  margin-top: 18px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 8px;

    .metric-pill {
      flex: 1 1 100%;
      padding: 10px 14px;
    }
  }

  .metric-pill {
    background: #f1f5f9;
    padding: 12px 18px;
    border-radius: $radius-md;
    display: flex;
    flex-direction: column;

    .metric-val {
      font-size: 22px;
      font-weight: 800;
      color: #0b7a3a;
      font-family: $font-family-heading;
    }

    .metric-lbl {
      font-size: 12px;
      color: $color-text-secondary;
      font-weight: 500;
    }
  }
}

.saving-badge {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: rgba(0, 230, 118, 0.12);
  border: 1px solid rgba(0, 230, 118, 0.3);
  padding: 12px 18px;
  border-radius: $radius-md;
  margin-top: 8px;

  .saving-num {
    font-size: 26px;
    font-weight: 800;
    color: $color-neon-green;
    font-family: $font-family-heading;
  }

  .saving-txt {
    font-size: 13px;
    color: #cbd5e1;
    line-height: 1.3;
  }
}

/* Coupon & Marketing Showcase */
.coupon-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 480px) {
    gap: 6px;

    .coupon-tab-btn {
      font-size: 12px;
      padding: 6px 12px;
    }
  }

  .coupon-tab-btn {
    background: #ffffff;
    border: 1px solid #cbd5e1;
    color: $color-text-dark;
    font-size: 13px;
    font-weight: 600;
    padding: 8px 16px;
    border-radius: $radius-full;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #0ebd60;
      color: #0b7a3a;
    }

    &.active {
      background: $color-graphite;
      color: #ffffff;
      border-color: $color-graphite;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }
}

.marketing-showcase {
  display: flex;
  gap: 36px;
  align-items: center;
  background: #ffffff;
  border-radius: $radius-md;
  padding: 32px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  margin-top: 16px;

  @media (max-width: 800px) {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
    padding: 20px 16px;
  }

  @media (max-width: 480px) {
    padding: 16px 12px;
  }

  .showcase-visual {
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: $radius-md;
    min-height: 280px;

    @media (max-width: 600px) {
      min-height: 220px;
    }

    .showcase-phone-img {
      max-height: 320px;
      max-width: 100%;
      height: auto;
      object-fit: contain;
      filter: drop-shadow(0 16px 28px rgba(0, 0, 0, 0.12));

      @media (max-width: 600px) {
        max-height: 240px;
      }
    }
  }

  .showcase-info {
    flex-grow: 1;

    .showcase-top {
      display: flex;
      gap: 10px;
      margin-bottom: 12px;

      .badge-accent {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        background: rgba(0, 230, 118, 0.15);
        color: #0b7a3a;
        padding: 4px 10px;
        border-radius: $radius-sm;
      }

      .badge-type {
        font-size: 11px;
        font-weight: 600;
        background: #f1f5f9;
        color: #475569;
        padding: 4px 10px;
        border-radius: $radius-sm;
      }
    }

    .showcase-title {
      font-family: $font-family-heading;
      font-size: 22px;
      font-weight: 800;
      color: $color-text-dark;
      margin-bottom: 8px;
    }

    .showcase-desc {
      font-size: 14px;
      line-height: 1.6;
      color: $color-text-secondary;
      margin-bottom: 16px;
    }

    .showcase-bullets {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 20px;

      .point-item {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 13px;
        color: $color-text-dark;

        .point-icon {
          color: #0ebd60;
          font-weight: 800;
        }
      }
    }

    .showcase-stats {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;

      .stat-box {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        padding: 10px 18px;
        border-radius: $radius-sm;
        display: flex;
        flex-direction: column;

        .stat-num {
          font-family: $font-family-heading;
          font-size: 18px;
          font-weight: 800;
          color: #0b7a3a;
        }

        .stat-label {
          font-size: 11px;
          color: $color-text-secondary;
        }
      }
    }
  }
}

.booking-features {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  .feat-pill {
    font-size: 12px;
    background: #f1f5f9;
    color: #475569;
    padding: 4px 12px;
    border-radius: $radius-full;
    font-weight: 600;
  }
}

.benefits-compact-list {
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
  display: flex;
  flex-direction: column;
  gap: 14px;

  li {
    display: flex;
    align-items: baseline;
    gap: 12px;
    font-size: 13px;
    color: #cbd5e1;

    strong {
      font-family: $font-family-heading;
      font-size: 18px;
      color: $color-neon-green;
      min-width: 50px;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
